import { z } from 'zod';
import { WorkspaceAttributeCacheSchema } from './attributes.js';
import { normalizeHeaderCell, parseCsv } from './csv.js';
import { VariableDefinitionsSchema, type VariableDefinitions } from './variable_definitions.js';
import { collectVariableValues, validateAttributeValues } from './variable_values.js';

// ─────────────────────────────────────────────────────────────────
// Email lists (audience) + subscribers
//
// A workspace owns lists; a list owns subscribers. Both the API
// (apps/server/src/routes/email-lists.ts) and the editor validate with
// these schemas, so request/response shapes cannot drift.
//
// Subscriber identity: `email` is required and unique within a list
// (case-insensitively, enforced by a unique index on lower(email)).
// `subscriberId` is an optional caller-supplied key — a CRM/import id —
// and is also unique within a list when present. `name` is optional.
//
// **Attributes** come in two halves, and the names are easy to mix up:
//   · a list holds the *definitions* of its merge fields
//     (`email_lists.attributes`, a VariableDefinitions record), and
//   · a subscriber holds its *values* for them (`subscribers.attributes`,
//     a plain `{ first_name: "Ada" }` record).
// A value is checked with `validateVariableValues` against the list's
// definitions: a value of the wrong type is refused, while a key the list does
// not define is ignored (the write succeeds without storing it), so an extra
// or stale field never fails a request.
// ─────────────────────────────────────────────────────────────────

export const SUBSCRIBER_EMAIL_MAX = 320;
export const SUBSCRIBER_ID_MAX = 255;
export const SUBSCRIBER_NAME_MAX = 255;
export const EMAIL_LIST_NAME_MAX = 255;

/**
 * Email validation. Kept free of `.transform()` so the OpenAPI generator can
 * introspect it; canonicalise with {@link normalizeSubscriberEmail} before
 * storing or comparing.
 */
export const SubscriberEmailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .max(SUBSCRIBER_EMAIL_MAX)
  .email('Invalid email address');

/**
 * Canonical stored/compared form of a subscriber email: trimmed and
 * lower-cased, matching the unique index on `lower(email)`.
 */
export function normalizeSubscriberEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Treat an empty or whitespace-only optional value as absent. */
export function blankToNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** How a subscriber entered its list: typed in, or brought in by CSV import. */
export const SubscriberSourceSchema = z.enum(['manual', 'csv']);
export type SubscriberSource = z.infer<typeof SubscriberSourceSchema>;

// ── Lists ─────────────────────────────────────────────────────────

export const CreateEmailListSchema = z.object({
  name: z.string().trim().min(1, 'List name is required').max(EMAIL_LIST_NAME_MAX),
  description: z.string().trim().max(2000).optional(),
});
export type CreateEmailListInput = z.infer<typeof CreateEmailListSchema>;

export const UpdateEmailListSchema = z.object({
  name: z.string().trim().min(1).max(EMAIL_LIST_NAME_MAX).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
});
export type UpdateEmailListInput = z.infer<typeof UpdateEmailListSchema>;

/** A list as returned by the API, with its current subscriber count. */
export const EmailListSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  subscriberCount: z.number().int(),
  /**
   * The merge fields this audience can be personalised with — a
   * VariableDefinitions record, exactly like an email's own variables
   * (`emails.variable_definition`). Read it tolerantly with
   * `parseVariableDefinitions`; write it whole via
   * PUT /api/email-lists/:id/attributes.
   */
  attributes: VariableDefinitionsSchema.default({}),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EmailList = z.infer<typeof EmailListSchema>;

/** Result of replacing a list's attributes wholesale. */
export const EmailListAttributesResultSchema = z.object({
  attributes: VariableDefinitionsSchema,
  /**
   * The workspace's rebuilt attribute cache. Returned with the write so the
   * editor can update the `{{…}}` picker without a second round-trip — the
   * cache is the only thing the block editor reads.
   */
  attributeCache: WorkspaceAttributeCacheSchema,
});
export type EmailListAttributesResult = z.infer<typeof EmailListAttributesResultSchema>;

export const EmailListListSchema = z.object({ data: z.array(EmailListSchema) });
export type EmailListList = z.infer<typeof EmailListListSchema>;

// ── Subscriber attributes (values) ────────────────────────────────

/**
 * One stored attribute value. The union is exactly what the definition types
 * can produce (`string`/`url`/`data_image_url` → string, `number`, `boolean`,
 * `array` → string[]), plus null for a cleared value — so this is the
 * transport shape, while the real rules come from the list's definitions.
 */
export const SubscriberAttributeValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.null(),
]);
export type SubscriberAttributeValue = z.infer<typeof SubscriberAttributeValueSchema>;

export const SubscriberAttributesSchema = z.record(z.string(), SubscriberAttributeValueSchema);
export type SubscriberAttributes = z.infer<typeof SubscriberAttributesSchema>;

/**
 * Read a stored attributes record tolerantly: keep the entries that conform
 * and drop the rest, so one hand-edited jsonb value cannot take a whole
 * subscriber (or a page of them) down. Mirrors `parseVariableDefinitions`.
 */
export function parseSubscriberAttributes(raw: unknown): SubscriberAttributes {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

  const values: SubscriberAttributes = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const parsed = SubscriberAttributeValueSchema.safeParse(value);
    if (parsed.success) values[key] = parsed.data;
  }
  return values;
}

// ── Subscribers ───────────────────────────────────────────────────

export const SubscriberSchema = z.object({
  id: z.string().uuid(),
  listId: z.string().uuid(),
  subscriberId: z.string().nullable(),
  email: z.string(),
  name: z.string().nullable(),
  /**
   * This contact's values for the list's attributes, keyed by attribute name.
   * Only keys the list defines can be set (see the create/update schemas).
   */
  attributes: SubscriberAttributesSchema,
  source: SubscriberSourceSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Subscriber = z.infer<typeof SubscriberSchema>;

export const CreateSubscriberSchema = z.object({
  /** Optional caller-supplied key (CRM id, import id…). */
  subscriberId: z.string().trim().max(SUBSCRIBER_ID_MAX).optional(),
  email: SubscriberEmailSchema,
  name: z.string().trim().max(SUBSCRIBER_NAME_MAX).optional(),
  /**
   * Values for the list's attributes. Checked against the list's definitions
   * with `validateVariableValues`: a value of the wrong type is a 422, and a
   * key the list does not define is ignored rather than refused. Omitted means
   * "no attributes yet".
   */
  attributes: SubscriberAttributesSchema.optional(),
});
export type CreateSubscriberInput = z.infer<typeof CreateSubscriberSchema>;

export const UpdateSubscriberSchema = z.object({
  subscriberId: z.string().trim().max(SUBSCRIBER_ID_MAX).nullable().optional(),
  email: SubscriberEmailSchema.optional(),
  name: z.string().trim().max(SUBSCRIBER_NAME_MAX).nullable().optional(),
  /**
   * Attribute values. Like an email's variables (PUT /api/emails/:id/variables)
   * the whole record is replaced by one request — which is what the editor
   * sends, and what makes "clear this attribute" just an omission. Omitted
   * leaves the stored values untouched.
   */
  attributes: SubscriberAttributesSchema.optional(),
});
export type UpdateSubscriberInput = z.infer<typeof UpdateSubscriberSchema>;

/**
 * Body of the attributes-only write the public API exposes
 * (`PUT /api/v1/lists/{listId}/subscribers/{subscriberRef}/attributes`).
 *
 * Required here, unlike {@link UpdateSubscriberSchema}: attributes are the one
 * thing that endpoint does, so a body without them would be a request that
 * silently changes nothing.
 */
export const UpdateSubscriberAttributesSchema = z.object({
  /** The full record, not a patch — an omitted key clears that attribute. */
  attributes: SubscriberAttributesSchema,
});
export type UpdateSubscriberAttributesInput = z.infer<typeof UpdateSubscriberAttributesSchema>;

export const SubscriberListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** Matches email, name or subscriberId (case-insensitive substring). */
  search: z.string().trim().max(200).optional(),
});
export type SubscriberListQuery = z.infer<typeof SubscriberListQuerySchema>;

export const PaginatedSubscribersSchema = z.object({
  data: z.array(SubscriberSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type PaginatedSubscribers = z.infer<typeof PaginatedSubscribersSchema>;

// ─────────────────────────────────────────────────────────────────
// CSV import
//
// The editor reads the chosen file (or pasted text) and posts the raw
// CSV text here; the server parses and validates it with the exact
// functions below so the preview the user approves is the import that
// runs. Column layout is detected from the header, guessed from the data
// when there is no usable header, and overridable per request.
// ─────────────────────────────────────────────────────────────────

/** Hard ceiling on rows accepted in one import; larger files are refused. */
export const MAX_SUBSCRIBER_IMPORT_ROWS = 20_000;
/** Hard ceiling on the posted CSV text (bytes ≈ chars). */
export const MAX_SUBSCRIBER_CSV_BYTES = 8 * 1024 * 1024;

/** The three fields we can fill from a CSV, all optional except email. */
export type SubscriberCsvField = 'subscriberId' | 'email';

/** Which CSV column feeds each field (0-based), or null when absent. */
export type CsvColumnMapping = Record<SubscriberCsvField, number | null>;

export const CsvColumnMappingSchema = z.object({
  subscriberId: z.number().int().min(0).nullable().optional(),
  email: z.number().int().min(0).nullable().optional(),
  name: z.number().int().min(0).nullable().optional(),
});

export const ImportSubscribersSchema = z.object({
  /** Raw CSV text (the file's contents, or pasted text). */
  csv: z.string().min(1, 'CSV content is required').max(MAX_SUBSCRIBER_CSV_BYTES),
  /** Original filename, recorded only for the response message. */
  fileName: z.string().max(255).optional(),
  /** True when the first record is a header row. Auto-detected when omitted. */
  hasHeader: z.boolean().optional(),
  /** Column override (0-based indexes); auto-detected when omitted. */
  mapping: CsvColumnMappingSchema.optional(),
  /**
   * Attribute-name → column override (0-based). Auto-detected by matching
   * header cells against the list's attribute names when omitted; the editor
   * sends back what its preview resolved so both sides agree exactly.
   */
  attributeMapping: z.record(z.string(), z.number().int().min(0)).optional(),
});
export type ImportSubscribersInput = z.infer<typeof ImportSubscribersSchema>;

/** Header aliases per field, matched against {@link normalizeHeaderCell}. */
export const CSV_FIELD_ALIASES: Record<SubscriberCsvField, readonly string[]> = {
  subscriberId: [
    'subscriber id',
    'subscriberid',
    'subscriber',
    'contact id',
    'contactid',
    'contact',
    'customer id',
    'customerid',
    'external id',
    'externalid',
    'user id',
    'userid',
    'member id',
    'memberid',
    'crm id',
    'id',
    'uid',
  ],
  email: [
    'email',
    'e mail',
    'email address',
    'emailaddress',
    'mail',
    'mail address',
    'mailaddress',
    'e mail address',
    'subscriber email',
    'contact email',
  ],
};

/** Why a CSV record was left out of the import. */
export const CSV_REJECT_REASONS = [
  'missing_email',
  'invalid_email',
  'too_long',
  'duplicate_email',
  'duplicate_subscriber_id',
  'invalid_attribute',
] as const;
export const CsvRejectReasonSchema = z.enum(CSV_REJECT_REASONS);
export type CsvRejectReason = z.infer<typeof CsvRejectReasonSchema>;

/** Human-readable explanation for each reject reason (used by the UI). */
export const CSV_REJECT_LABELS: Record<CsvRejectReason, string> = {
  missing_email: 'No email address in the mapped email column',
  invalid_email: 'Not a valid email address',
  too_long: 'A value is longer than the column allows',
  duplicate_email: 'Duplicate email within the file',
  duplicate_subscriber_id: 'Duplicate subscriber id within the file',
  invalid_attribute: 'An attribute value does not fit its definition',
};

/** A validated subscriber row ready to insert. */
export const CsvSubscriberRowSchema = z.object({
  /** 1-based record number in the file (row 1 is the header when present). */
  row: z.number().int(),
  subscriberId: z.string().nullable(),
  email: z.string(),
  /** Attribute values from the mapped columns, already typed and validated. */
  attributes: SubscriberAttributesSchema,
});
export type CsvSubscriberRow = z.infer<typeof CsvSubscriberRowSchema>;

/** A record that will not be imported, and why. */
export const CsvRowErrorSchema = z.object({
  row: z.number().int(),
  email: z.string().nullable(),
  reason: CsvRejectReasonSchema,
  /** The underlying problem, e.g. which attribute and why (invalid_attribute). */
  message: z.string().optional(),
});
export type CsvRowError = z.infer<typeof CsvRowErrorSchema>;

/** Result of parsing subscriber CSV text — the preview shown before import. */
export const SubscriberCsvParseSchema = z.object({
  ok: z.literal(true),
  delimiter: z.string(),
  hasHeader: z.boolean(),
  /** Header cells as they appeared in the file (null when there is no header). */
  header: z.array(z.string()).nullable(),
  /** The column layout used for this parse. */
  mapping: z.object({
    subscriberId: z.number().int().nullable(),
    email: z.number().int().nullable(),
  }),
  /** How the mapping was decided — drives the "check your columns" hint. */
  mappingSource: z.enum(['header', 'sniffed', 'override']),
  /**
   * Attribute name → column, for the attributes this file carries. Only
   * columns whose header names an attribute the list defines are mapped.
   */
  attributeMapping: z.record(z.string(), z.number().int()),
  /** Data records found (header excluded). */
  totalRows: z.number().int(),
  /** Distinct, valid rows — what an unconstrained import would add. */
  valid: z.array(CsvSubscriberRowSchema),
  /** Records left out, with their row numbers and reasons (capped — see rejectedTotal). */
  rejected: z.array(CsvRowErrorSchema),
  /** Exact number of rejected records, even when `rejected` is capped. */
  rejectedTotal: z.number().int(),
  /**
   * Exact rejected count per reason, so summaries never under-report even
   * when `rejected` is capped.
   */
  rejectedCounts: z.object({
    missing_email: z.number().int(),
    invalid_email: z.number().int(),
    too_long: z.number().int(),
    duplicate_email: z.number().int(),
    duplicate_subscriber_id: z.number().int(),
    invalid_attribute: z.number().int(),
  }),
});

/** Parsing refused the file outright (unusable columns, or too many rows). */
export const SubscriberCsvParseErrorSchema = z.object({
  ok: z.literal(false),
  error: z.string(),
});

export const SubscriberCsvParseResultSchema = z.discriminatedUnion('ok', [
  SubscriberCsvParseSchema,
  SubscriberCsvParseErrorSchema,
]);
export type SubscriberCsvParse = z.infer<typeof SubscriberCsvParseSchema>;
export type SubscriberCsvParseResult = z.infer<typeof SubscriberCsvParseResultSchema>;

/** Per-row summary returned by POST /api/email-lists/:id/import. */
export const ImportSubscribersResultSchema = z.object({
  /** Data records found in the file (header excluded). */
  totalRows: z.number().int(),
  /** Rows inserted by this import. */
  imported: z.number().int(),
  /** Rows skipped because the list already held that email or subscriber id. */
  duplicates: z.number().int(),
  /** Rows that could not be parsed or validated. */
  invalid: z.number().int(),
  /** Valid rows the plan's subscriber cap did not leave room for. */
  overLimit: z.number().int(),
  /** Details for the affected rows (capped — see errorsTruncated). */
  errors: z.array(
    z.object({
      row: z.number().int(),
      email: z.string().nullable(),
      reason: z.union([
        CsvRejectReasonSchema,
        z.literal('already_in_list'),
        z.literal('limit_reached'),
      ]),
      message: z.string().optional(),
    }),
  ),
  /** True when more rows were rejected than `errors` lists. */
  errorsTruncated: z.boolean(),
  /** The list after the import, with its updated subscriber count. */
  list: EmailListSchema,
});
export type ImportSubscribersResult = z.infer<typeof ImportSubscribersResultSchema>;

// ─────────────────────────────────────────────────────────────────
// CSV → subscriber mapping
// ─────────────────────────────────────────────────────────────────

/** Cap on the returned `rejected` array — counts stay exact, details are capped. */
const MAX_REPORTED_REJECTIONS = 100;

const isEmptyMapping = (mapping: CsvColumnMapping): boolean => mapping.email === null;

/** Map each field to a column by matching header cells against the aliases. */
function mappingFromHeader(header: string[]): CsvColumnMapping | null {
  const mapping: CsvColumnMapping = { subscriberId: null, email: null };
  const normalised = header.map(normalizeHeaderCell);

  for (const field of ['email', 'subscriberId'] as const) {
    const index = normalised.findIndex((cell) => CSV_FIELD_ALIASES[field].includes(cell));
    if (index !== -1) mapping[field] = index;
  }

  // A header we cannot find an email column in is not a usable header.
  return isEmptyMapping(mapping) ? null : mapping;
}

/**
 * Map attribute names to columns by matching header cells to attribute names
 * through {@link normalizeHeaderCell}, so "First Name" and `first_name` land
 * on the same column without a mapping UI. Columns the fixed fields already
 * claimed are skipped — one column cannot feed two things — and keys are
 * visited in sorted order so a duplicate header resolves deterministically.
 */
function attributeMappingFromHeader(
  header: string[],
  definitions: VariableDefinitions,
  taken: ReadonlySet<number>,
): Record<string, number> {
  const mapping: Record<string, number> = {};
  const normalised = header.map(normalizeHeaderCell);
  const claimed = new Set(taken);

  for (const key of Object.keys(definitions).sort()) {
    const index = normalised.indexOf(normalizeHeaderCell(key));
    if (index === -1 || claimed.has(index)) continue;
    mapping[key] = index;
    claimed.add(index);
  }
  return mapping;
}

/** Ratio of rows whose cell in `column` is a valid-looking email. */
function emailColumnScore(rows: string[][], column: number): number {
  let seen = 0;
  let valid = 0;
  for (const row of rows) {
    const value = (row[column] ?? '').trim();
    if (!value) continue;
    seen += 1;
    if (SubscriberEmailSchema.safeParse(value).success) valid += 1;
  }
  return seen === 0 ? 0 : valid / seen;
}

/**
 * Whether a value reads like a person's name rather than an opaque id:
 * letters only, either several words ("Ada Lovelace") or one capitalised
 * word ("Ian"). Ids ("crm_1042", "user-88") contain digits or punctuation
 * and fail both tests.
 */
function looksLikePersonName(value: string): boolean {
  if (/^[A-Za-z][A-Za-z'’.-]*(?:\s+[A-Za-z][A-Za-z'’.-]*)+$/.test(value)) return true;
  return /^[A-Z][a-z'’.-]+$/.test(value);
}

/** Ratio of rows whose cell in `column` reads like a person's name. */
function nameColumnScore(rows: string[][], column: number): number {
  let seen = 0;
  let valid = 0;
  for (const row of rows) {
    const value = (row[column] ?? '').trim();
    if (!value) continue;
    seen += 1;
    if (looksLikePersonName(value)) valid += 1;
  }
  return seen === 0 ? 0 : valid / seen;
}

/**
 * Guess the layout of a headerless file: the column that is mostly valid
 * emails becomes `email`; of the columns left, one that reads like names
 * becomes `name` and the rest fill `subscriberId`. A single-column file is
 * treated as emails only — a bare list of addresses is the common case and
 * must not be misread as an id column.
 */
function sniffMapping(rows: string[][]): CsvColumnMapping | null {
  const width = rows.reduce((max, row) => Math.max(max, row.length), 0);
  if (width === 0) return null;

  let emailColumn: number | null = null;
  let bestScore = 0;
  for (let column = 0; column < width; column += 1) {
    const score = emailColumnScore(rows, column);
    if (score > bestScore) {
      bestScore = score;
      emailColumn = column;
    }
  }
  // Require a clear majority so a name column is never mistaken for emails.
  if (emailColumn === null || bestScore < 0.5) return null;

  const rest = Array.from({ length: width }, (_, i) => i).filter((i) => i !== emailColumn);
  let nameColumn: number | null = null;
  let bestNameScore = 0;
  for (const column of rest) {
    const score = nameColumnScore(rows, column);
    if (score > bestNameScore) {
      bestNameScore = score;
      nameColumn = column;
    }
  }

  return {
    subscriberId: rest.find((column) => column !== nameColumn) ?? null,
    email: emailColumn,
  };
}

/**
 * Parse subscriber CSV text into validated rows, deduplicated within the
 * file, plus the records that were rejected and why.
 *
 * `options.mapping` (0-based column indexes) bypasses detection entirely;
 * `options.hasHeader` overrides header detection. Both are what the
 * editor sends back once the user has confirmed the preview.
 */
export function parseSubscriberCsv(
  text: string,
  options: {
    /** Column override; only the fields pinned are used (nulls are ignored). */
    mapping?: Partial<Record<SubscriberCsvField, number | null | undefined>>;
    hasHeader?: boolean;
    /**
     * The list's attribute definitions. When given, columns whose header names
     * an attribute are imported into it, and every row's values are validated
     * against these definitions — exactly what the route enforces on write.
     */
    definitions?: VariableDefinitions;
    /** Attribute-name → column override; detected from the header when omitted. */
    attributeMapping?: Record<string, number | null | undefined>;
  } = {},
): SubscriberCsvParseResult {
  const table = parseCsv(text);
  const all = table.rows;
  if (all.length === 0) return { ok: false, error: 'That file has no rows.' };

  const firstRow = all[0]!;
  const headerGuess = mappingFromHeader(firstRow);

  // Column indexes the caller pinned (only those actually provided).
  const provided = Object.entries(options.mapping ?? {}).filter(
    ([, value]) => typeof value === 'number',
  ) as [SubscriberCsvField, number][];
  // Any pinned column means "use exactly this layout" — never mix a
  // user-chosen column with a guess for the others.
  const hasOverride = provided.length > 0;

  let hasHeader: boolean;
  let mapping: CsvColumnMapping;
  let mappingSource: 'header' | 'sniffed' | 'override';

  if (hasOverride) {
    mapping = { subscriberId: null, email: null };
    for (const [field, index] of provided) mapping[field] = index;
    if (mapping.email === null) {
      return { ok: false, error: 'Pick which column holds the email address.' };
    }
    // With an explicit layout we still need to know whether row 1 is data.
    hasHeader = options.hasHeader ?? headerGuess !== null;
    mappingSource = 'override';
  } else if (options.hasHeader === false) {
    const sniffed = sniffMapping(all);
    if (!sniffed) {
      return {
        ok: false,
        error:
          'Could not find an email column. Add a header row (subscriberId, email, name) or pick the columns yourself.',
      };
    }
    mapping = sniffed;
    hasHeader = false;
    mappingSource = 'sniffed';
  } else if (headerGuess) {
    mapping = headerGuess;
    hasHeader = true;
    mappingSource = 'header';
  } else {
    const sniffed = sniffMapping(all);
    if (!sniffed) {
      return {
        ok: false,
        error:
          'Could not find an email column. Add a header row (subscriberId, email, name) or pick the columns yourself.',
      };
    }
    mapping = sniffed;
    hasHeader = false;
    mappingSource = 'sniffed';
  }

  // ── Attribute columns ─────────────────────────────────────────
  // An explicit mapping wins; otherwise a header is needed to recognise a
  // column by name (a headerless file can only carry attributes if the caller
  // says which column is which).
  const definitions = options.definitions ?? {};
  const pinnedAttributes = Object.entries(options.attributeMapping ?? {}).filter(
    ([, index]) => typeof index === 'number' && index >= 0,
  ) as [string, number][];

  let attributeMapping: Record<string, number>;
  if (pinnedAttributes.length > 0) {
    attributeMapping = Object.fromEntries(pinnedAttributes);
  } else if (hasHeader) {
    const used = new Set(
      Object.values(mapping).filter((column): column is number => column !== null),
    );
    attributeMapping = attributeMappingFromHeader(firstRow, definitions, used);
  } else {
    attributeMapping = {};
  }

  const dataRows = hasHeader ? all.slice(1) : all;
  const offset = hasHeader ? 1 : 0;

  if (dataRows.length > MAX_SUBSCRIBER_IMPORT_ROWS) {
    return {
      ok: false,
      error: `That file has ${dataRows.length.toLocaleString()} rows — the limit for one import is ${MAX_SUBSCRIBER_IMPORT_ROWS.toLocaleString()}. Split it and import in batches.`,
    };
  }

  const valid: CsvSubscriberRow[] = [];
  const rejected: CsvRowError[] = [];
  const rejectedCounts: Record<CsvRejectReason, number> = {
    missing_email: 0,
    invalid_email: 0,
    too_long: 0,
    duplicate_email: 0,
    duplicate_subscriber_id: 0,
    invalid_attribute: 0,
  };
  const seenEmails = new Set<string>();
  const seenSubscriberIds = new Set<string>();

  const reject = (
    row: number,
    email: string | null,
    reason: CsvRejectReason,
    message?: string,
  ): void => {
    rejectedCounts[reason] += 1;
    // The detail list is capped for the response; the counts above stay exact.
    if (rejected.length < MAX_REPORTED_REJECTIONS) {
      rejected.push(
        message === undefined ? { row, email, reason } : { row, email, reason, message },
      );
    }
  };

  const cell = (row: string[], column: number | null): string =>
    column === null ? '' : (row[column] ?? '').trim();

  dataRows.forEach((row, index) => {
    // 1-based record number in the file, header included, so messages
    // point at the line the user sees in their spreadsheet.
    const rowNumber = index + 1 + offset;

    const rawEmail = cell(row, mapping.email);
    const rawSubscriberId = cell(row, mapping.subscriberId);

    if (rawEmail === '') {
      // The record survived CSV parsing, so it does hold data somewhere —
      // it just has nothing in the mapped email column.
      reject(rowNumber, null, 'missing_email');
      return;
    }
    if (rawEmail.length > SUBSCRIBER_EMAIL_MAX) {
      reject(rowNumber, null, 'too_long');
      return;
    }
    if (rawSubscriberId.length > SUBSCRIBER_ID_MAX) {
      reject(rowNumber, rawEmail, 'too_long');
      return;
    }

    const parsedEmail = SubscriberEmailSchema.safeParse(rawEmail);
    if (!parsedEmail.success) {
      reject(rowNumber, rawEmail.slice(0, SUBSCRIBER_EMAIL_MAX), 'invalid_email');
      return;
    }
    const email = normalizeSubscriberEmail(parsedEmail.data);

    const subscriberId = rawSubscriberId === '' ? null : rawSubscriberId;

    // Attribute columns → typed values → checked against the list's
    // definitions, so a row whose values do not fit its own audience schema is
    // reported here rather than landing half-personalised in the list.
    const attributeInputs: Record<string, string> = {};
    for (const [key, column] of Object.entries(attributeMapping)) {
      attributeInputs[key] = cell(row, column);
    }
    const checkedAttributes = validateAttributeValues(
      definitions,
      collectVariableValues(definitions, attributeInputs),
    );
    if (!checkedAttributes.success) {
      reject(
        rowNumber,
        email,
        'invalid_attribute',
        checkedAttributes.errors
          .map((issue) => `${issue.path.join('.') || 'value'}: ${issue.message}`)
          .join('; '),
      );
      return;
    }

    if (seenEmails.has(email)) {
      reject(rowNumber, email, 'duplicate_email');
      return;
    }
    if (subscriberId !== null && seenSubscriberIds.has(subscriberId)) {
      reject(rowNumber, email, 'duplicate_subscriber_id');
      return;
    }

    seenEmails.add(email);
    if (subscriberId !== null) seenSubscriberIds.add(subscriberId);
    valid.push({
      row: rowNumber,
      subscriberId,
      email,
      attributes: checkedAttributes.data as SubscriberAttributes,
    });
  });

  return {
    ok: true,
    delimiter: table.delimiter,
    hasHeader,
    header: hasHeader ? firstRow : null,
    mapping,
    mappingSource,
    attributeMapping,
    totalRows: dataRows.length,
    valid,
    rejected,
    rejectedTotal: Object.values(rejectedCounts).reduce((sum, n) => sum + n, 0),
    rejectedCounts,
  };
}

/** Column count of the widest record, for building mapping dropdowns. */
export function csvColumnCount(text: string): number {
  return parseCsv(text).rows.reduce((max, row) => Math.max(max, row.length), 0);
}

/**
 * A ready-to-edit CSV template offered in the import dialog. `example` is
 * shown in the UI; `csv` is downloaded.
 */
export const SUBSCRIBER_CSV_TEMPLATE = {
  example: 'subscriberId,email,name\ncrm_1042,ada@example.com,Ada Lovelace',
  csv: 'subscriberId,email,name\ncrm_1042,ada@example.com,Ada Lovelace\ncrm_1043,grace@example.com,Grace Hopper\n',
} as const;
